import { FC, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';

import MicIcon from 'assets/icons/mic.svg';
import MicOffIcon from 'assets/icons/mic-off.svg';
import AssistantIcon from 'assets/icons/nav/support.svg';
import { store } from 'store';
import {
  getMicAnalyser,
  getServerAnalyser,
  isConnected,
  isConnecting,
  setMicMuted,
  startVoice,
  stopVoice,
} from 'store/assistant/voiceManager';

import * as S from './AssistantButton.styles';
import { setupNeonScene } from './neonScene';
import { setupSpaceAura } from './spaceAura';

const BTN_DESKTOP = 58;
const BTN_MOBILE = 52;

// ── Audio intensity helper ──────────────────────────────────────────────────

function getIntensity(analyser: AnalyserNode | null): number {
  if (!analyser) return 0;
  const data = new Uint8Array(analyser.frequencyBinCount);
  analyser.getByteFrequencyData(data);
  let sum = 0;
  for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
  return Math.min(1, (Math.sqrt(sum / data.length) * 1.5) / 255);
}

// ── Satellites sub-component ────────────────────────────────────────────────

const MicSatellite: FC<{ onMicToggle: () => void; muted: boolean }> = ({ onMicToggle, muted }) => (
  <S.SatelliteBtn onClick={onMicToggle}>{muted ? <MicOffIcon /> : <MicIcon />}</S.SatelliteBtn>
);

// ── Main component ──────────────────────────────────────────────────────────

export const AssistantButton: FC = observer(() => {
  const { voiceState, isMicMuted, setAssistantMode, setVoiceState } = store.assistantStore;
  const { isMobile } = store.commonStore;

  const btnSize = isMobile ? BTN_MOBILE : BTN_DESKTOP;
  const isActive = voiceState === 'active';
  const isLoading = voiceState === 'connecting';

  // refs — button 1
  const eqCanvasRef = useRef<HTMLCanvasElement>(null);
  const eqAnimRef = useRef<number | null>(null);

  // refs — button 2 (space)
  const spaceCanvasRef = useRef<HTMLCanvasElement>(null);
  const spaceRef = useRef<ReturnType<typeof setupSpaceAura> | null>(null);

  // refs — button 3 (neon)
  const neonCanvasRef = useRef<HTMLCanvasElement>(null);
  const neonRef = useRef<ReturnType<typeof setupNeonScene> | null>(null);

  // ── Shared handlers ─────────────────────────────────────────────────────

  const handleClick = useCallback(async () => {
    if (isConnecting()) return;

    neonRef.current?.triggerBurst();

    if (isConnected()) {
      stopVoice();
      setAssistantMode(null);
      return;
    }

    setAssistantMode('voice');
    setVoiceState('connecting');
    await startVoice();
  }, [setAssistantMode, setVoiceState]);

  const handleMicToggle = useCallback(() => {
    setMicMuted(!isMicMuted);
  }, [isMicMuted]);

  // ── Button 1: EQ canvas ─────────────────────────────────────────────────

  useEffect(() => {
    if (!isActive) {
      if (eqAnimRef.current) {
        cancelAnimationFrame(eqAnimRef.current);
        eqAnimRef.current = null;
      }
      return;
    }

    const canvas = eqCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const draw = () => {
      eqAnimRef.current = requestAnimationFrame(draw);
      const w = canvas.width;
      const h = canvas.height;
      const cx = w / 2;
      const cy = h / 2;
      ctx.clearRect(0, 0, w, h);

      const micAn = getMicAnalyser();
      const srvAn = getServerAnalyser();
      const barCount = 36;
      const innerR = w * 0.22;
      const maxBarLen = w * 0.2;

      if (micAn) {
        const bufLen = micAn.frequencyBinCount;
        const data = new Uint8Array(bufLen);
        micAn.getByteFrequencyData(data);
        for (let i = 0; i < barCount; i++) {
          const angle = (i / barCount) * Math.PI * 2 - Math.PI / 2;
          const idx = Math.floor((i * bufLen * 0.5) / barCount);
          const val = data[idx] / 255;
          const barLen = Math.max(1.5, val * maxBarLen);
          const x1 = cx + Math.cos(angle) * innerR;
          const y1 = cy + Math.sin(angle) * innerR;
          const x2 = cx + Math.cos(angle) * (innerR + barLen);
          const y2 = cy + Math.sin(angle) * (innerR + barLen);
          const t = i / barCount;
          const g = Math.floor((255 - 115 * t) * (0.7 + 0.3 * val));
          const b = Math.floor((120 + 135 * t) * (0.5 + 0.5 * val));
          ctx.strokeStyle = `rgba(0, ${g}, ${b}, ${0.5 + val * 0.5})`;
          ctx.lineWidth = 2;
          ctx.lineCap = 'round';
          ctx.beginPath();
          ctx.moveTo(x1, y1);
          ctx.lineTo(x2, y2);
          ctx.stroke();
        }
      }

      if (srvAn) {
        const srvData = new Uint8Array(srvAn.frequencyBinCount);
        srvAn.getByteFrequencyData(srvData);
        const rms = Math.sqrt(srvData.reduce((a, v) => a + v * v, 0) / srvData.length);
        const intensity = Math.min(1, (rms * 1.5) / 255);
        const coreR = w * 0.15 + intensity * w * 0.04;
        const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, coreR);
        gradient.addColorStop(0, `rgba(0, 255, 160, ${0.35 + intensity * 0.4})`);
        gradient.addColorStop(0.6, `rgba(0, 180, 255, ${0.15 + intensity * 0.2})`);
        gradient.addColorStop(1, 'rgba(0, 140, 255, 0)');
        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(cx, cy, coreR, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    draw();
    return () => {
      if (eqAnimRef.current) {
        cancelAnimationFrame(eqAnimRef.current);
        eqAnimRef.current = null;
      }
    };
  }, [isActive]);

  // ── Button 2: Space aura setup ──────────────────────────────────────────

  useEffect(() => {
    const canvas = spaceCanvasRef.current;
    if (!canvas) return;
    const engine = setupSpaceAura(canvas, 170);
    spaceRef.current = engine;
    return () => {
      engine.dispose();
      spaceRef.current = null;
    };
  }, []);

  // sync active state
  useEffect(() => {
    spaceRef.current?.setActive(isActive);
  }, [isActive]);

  // ── Button 3: Neon scene setup ──────────────────────────────────────────

  useEffect(() => {
    const canvas = neonCanvasRef.current;
    if (!canvas) return;
    const engine = setupNeonScene(canvas, 260);
    neonRef.current = engine;
    return () => {
      engine.dispose();
      neonRef.current = null;
    };
  }, []);

  // sync active state
  useEffect(() => {
    neonRef.current?.setActive(isActive);
  }, [isActive]);

  // hover handlers for neon
  const handleNeonEnter = useCallback(() => neonRef.current?.setHovered(true), []);
  const handleNeonLeave = useCallback(() => neonRef.current?.setHovered(false), []);

  // ── Audio polling for buttons 2 & 3 ─────────────────────────────────────

  useEffect(() => {
    if (!isActive) return;
    let animId: number;
    const poll = () => {
      animId = requestAnimationFrame(poll);
      const mic = getIntensity(getMicAnalyser());
      const srv = getIntensity(getServerAnalyser());
      spaceRef.current?.setAudio(mic, srv);
      neonRef.current?.setAudio(mic, srv);
    };
    poll();
    return () => cancelAnimationFrame(animId);
  }, [isActive]);

  // ── Render ──────────────────────────────────────────────────────────────

  return (
    <S.Root>
      {/* ── Button 1: Original ────────────────────────────────────────── */}
      <S.ButtonSlot $size={btnSize}>
        <S.MainButton $size={btnSize} $active={isActive || isLoading} onClick={handleClick}>
          {isActive ? (
            <>
              <S.OrbRing2 />
              <S.OrbRing />
              <S.OrbCore />
              <S.EqCanvas
                ref={eqCanvasRef}
                width={btnSize * 2}
                height={btnSize * 2}
                $size={btnSize}
              />
            </>
          ) : (
            <AssistantIcon />
          )}
        </S.MainButton>
        {isActive && <MicSatellite onMicToggle={handleMicToggle} muted={isMicMuted} />}
        <S.Label>Sophie</S.Label>
      </S.ButtonSlot>

      {/* ── Button 2: Space ───────────────────────────────────────────── */}
      <S.ButtonSlot $size={btnSize}>
        <S.SpaceAuraCanvas ref={spaceCanvasRef} />
        <S.SpaceButton $size={btnSize} $active={isActive || isLoading} onClick={handleClick}>
          <AssistantIcon />
        </S.SpaceButton>
        {isActive && <MicSatellite onMicToggle={handleMicToggle} muted={isMicMuted} />}
        <S.Label>Sophie</S.Label>
      </S.ButtonSlot>

      {/* ── Button 3: Neon ────────────────────────────────────────────── */}
      <S.ButtonSlot $size={btnSize}>
        <S.NeonCanvasWrap>
          <S.NeonCanvas ref={neonCanvasRef} />
        </S.NeonCanvasWrap>
        <S.NeonButtonEl
          $size={btnSize}
          $active={isActive || isLoading}
          onClick={handleClick}
          onMouseEnter={handleNeonEnter}
          onMouseLeave={handleNeonLeave}
        >
          <AssistantIcon />
        </S.NeonButtonEl>
        {isActive && <MicSatellite onMicToggle={handleMicToggle} muted={isMicMuted} />}
        <S.Label>Sophie</S.Label>
      </S.ButtonSlot>
    </S.Root>
  );
});
